import { PwaInstallPlatform } from './pwa-install-platform';

/**
 * Short, per-browser "add to home screen" steps for the install prompt. The long-form version is
 * the FAQ entry `how-do-i-add-sunny-street-to-my-smartphones-homescreen` (auth/faq/faq.content.ts),
 * and the login intro deck shows the one-line summary; keep the three in step when a browser
 * moves its menus.
 *
 * A step renders as `text [icon] textAfter`, so an icon can sit inline where the user has to look.
 */
export interface PwaInstallStep {
  text: string;
  icon?: string;
  iconLabel?: string;
  textAfter?: string;
}

export interface PwaInstallInstructions {
  title: string;
  lead: string;
  steps: readonly PwaInstallStep[];
  footnote?: string;
}

const IOS_FINISH_STEPS: readonly PwaInstallStep[] = [
  { text: 'Tap “Add to Home Screen”. Scroll down or tap “View More” if you don’t see it.' },
  { text: 'Tap Add. Sunny Street opens from your home screen like an app.' },
];

const IOS_SAFARI: PwaInstallInstructions = {
  title: 'Add Sunny Street to your home screen',
  lead: 'One tap from your home screen, full screen, with notifications.',
  steps: [
    {
      text: 'Tap Share',
      icon: 'share-outline',
      iconLabel: 'Share',
      textAfter: 'in Safari’s toolbar. On iOS 26, tap ⋯ first, then Share.',
    },
    ...IOS_FINISH_STEPS,
  ],
};

const IOS_OTHER: PwaInstallInstructions = {
  title: 'Add Sunny Street to your home screen',
  lead: 'One tap from your home screen, full screen, with notifications.',
  steps: [
    {
      text: 'Tap Share',
      icon: 'share-outline',
      iconLabel: 'Share',
      textAfter: 'in the address bar (Chrome) or the browser menu (Firefox, Edge).',
    },
    ...IOS_FINISH_STEPS,
  ],
  footnote: 'No “Add to Home Screen” option? Open sunnystreet.app in Safari and add it from there.',
};

const ANDROID_MANUAL: PwaInstallInstructions = {
  title: 'Install Sunny Street',
  lead: 'One tap from your home screen, full screen, with notifications.',
  steps: [
    {
      text: 'Tap the menu',
      icon: 'ellipsis-vertical',
      iconLabel: 'Menu',
      textAfter: 'in the top-right corner of your browser.',
    },
    { text: 'Tap “Install app” or “Add to Home screen”.' },
    { text: 'Tap Install. Sunny Street appears on your home screen like an app.' },
  ],
};

const IN_APP_BROWSER: PwaInstallInstructions = {
  title: 'Open Sunny Street in your browser',
  lead: 'This app’s built-in browser can’t add Sunny Street to your home screen.',
  steps: [
    {
      text: 'Tap',
      icon: 'ellipsis-horizontal',
      iconLabel: 'More options',
      textAfter: 'in the top-right corner (or bottom-right).',
    },
    { text: 'Choose “Open in browser”, “Open in Safari” or “Open in Chrome”.' },
    { text: 'Add Sunny Street to your home screen from there.' },
  ],
};

/** Shown on Android Chromium when we hold a deferred `beforeinstallprompt` event. */
export const ANDROID_PROMPT_INSTRUCTIONS: PwaInstallInstructions = {
  title: 'Install Sunny Street',
  lead: 'Get one-tap access from your home screen, full screen, with notifications.',
  steps: [],
};

export function getPwaInstallInstructions(platform: PwaInstallPlatform): PwaInstallInstructions | null {
  switch (platform) {
    case 'ios-safari':
      return IOS_SAFARI;
    case 'ios-other':
      return IOS_OTHER;
    case 'android-chromium':
    case 'android-other':
      return ANDROID_MANUAL;
    case 'in-app-browser':
      return IN_APP_BROWSER;
    default:
      return null;
  }
}
